import { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/lib/supabase";

export interface CatalogStatusEntry {
  id: number;
  name: string;
  slug: string;
  status: string;
  category: string | null;
}

interface UseProductCatalogStatusResult {
  entries: CatalogStatusEntry[];
  bySlug: Record<string, CatalogStatusEntry>;
  byName: Record<string, CatalogStatusEntry>;
  loading: boolean;
  error: string | null;
  reload: () => void;
}

// 判断产品是否已停止授权（下架 / 草稿）——只有 active 才算在售。
export function isDelisted(status: string | null | undefined): boolean {
  return Boolean(status) && status !== "active";
}

// 下架兜底：从完整目录中挑出「同分类、在售、且不在排除名单」的产品作为推荐。
export function recommendSameCategory(
  entries: CatalogStatusEntry[],
  category: string | null | undefined,
  excludeSlugs: string[] = [],
  limit = 3,
): CatalogStatusEntry[] {
  if (!category) return [];
  const excluded = new Set(excludeSlugs);
  return entries
    .filter(
      (entry) =>
        entry.category === category &&
        entry.status === "active" &&
        !excluded.has(entry.slug),
    )
    .slice(0, limit);
}

// 读取完整产品目录（含未上架 / 草稿），用于客户侧对已下架产品做兜底提示。
export function useProductCatalogStatus(): UseProductCatalogStatusResult {
  const [entries, setEntries] = useState<CatalogStatusEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data, error: queryError } = await supabase
        .from("software_products")
        .select("id, name, slug, status, category");
      if (queryError) throw queryError;
      setEntries((data as CatalogStatusEntry[]) ?? []);
    } catch (err) {
      setError(err instanceof Error ? err.message : "产品状态加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const { bySlug, byName } = useMemo(() => {
    const slugMap: Record<string, CatalogStatusEntry> = {};
    const nameMap: Record<string, CatalogStatusEntry> = {};
    entries.forEach((entry) => {
      if (entry.slug) slugMap[entry.slug] = entry;
      if (entry.name) nameMap[entry.name] = entry;
    });
    return { bySlug: slugMap, byName: nameMap };
  }, [entries]);

  return { entries, bySlug, byName, loading, error, reload: load };
}